import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit as fbLimit,
  orderBy,
  query,
  where,
  type Firestore,
} from 'firebase/firestore';
import type { BillingCharge } from '@kawai/shared';
import { OFFLINE_PAYMENT_METHODS } from '@kawai/shared';
import { db as defaultDb } from '../firebase.js';

/**
 * 1 テナント分の請求履歴を取得する上限。
 * 請求は初期費用 1 回 + 月額（月 1 件ペース）程度のため、200 件で十数年分を賄える。
 * これを超える規模が出たら cursor pagination（auditLogs.ts 方式）へ拡張する。
 */
export const BILLING_CHARGES_LIMIT = 200;

/**
 * TP-25: billing_charges を tenantId スコープで新しい順に取得（read 専用 / saas-admin 用）。
 *
 * - Firestore Rules: read は `isAdmin() && belongsToMyTenant()`（saas-admin はバイパスで横断可）
 * - composite index `(tenantId ASC, createdAt DESC)` は配備済み（firestore.indexes.json）
 * - テナント別明細のため saas-admin でも `where('tenantId','==',tenantId)` を常に付与
 *   （index にも一致し、テナントを跨いだ取得を構造的に避ける）
 * - 引数 `db` はテスト時に Firestore モックを注入するため optional
 */
export async function fetchBillingChargesByTenant(
  tenantId: string,
  db: Firestore = defaultDb,
): Promise<BillingCharge[]> {
  const snap = await getDocs(
    query(
      collection(db, 'billing_charges'),
      where('tenantId', '==', tenantId),
      orderBy('createdAt', 'desc'),
      fbLimit(BILLING_CHARGES_LIMIT),
    ),
  );
  return snap.docs.map((d) => ({
    ...(d.data() as Omit<BillingCharge, 'id'>),
    // doc id を真実源にする（data.id と一致するが防御的に上書き）。
    id: d.id,
  }));
}

/**
 * TP-27/TP-34: 手動請求（銀行振込・口座振替）を全テナント横断で新しい順に取得
 * （saas-admin の請求管理ページ用）。
 *
 * - Firestore Rules: saas-admin は `belongsToMyTenant()` バイパスで横断 read 可
 *   （tenant-admin が呼ぶと自テナント外の doc で permission-denied になるが、
 *   本 repository は SaasAdminGuard 配下の請求管理ページ専用）
 * - `in` クエリは等値の合併のため composite index `(paymentMethod ASC, createdAt DESC)`
 *   （firestore.indexes.json 配備済み）をそのまま充足する（index 追加不要）
 * - 件数は月次 × テナント数の低頻度のため BILLING_CHARGES_LIMIT を流用
 */
export async function fetchBankTransferCharges(
  db: Firestore = defaultDb,
): Promise<BillingCharge[]> {
  const snap = await getDocs(
    query(
      collection(db, 'billing_charges'),
      where('paymentMethod', 'in', [...OFFLINE_PAYMENT_METHODS]),
      orderBy('createdAt', 'desc'),
      fbLimit(BILLING_CHARGES_LIMIT),
    ),
  );
  return snap.docs.map((d) => ({
    ...(d.data() as Omit<BillingCharge, 'id'>),
    id: d.id,
  }));
}

/**
 * TP-26: billing_charges 1 件を doc id で取得（帳票の再発行用 / O(1) read）。
 *
 * 一覧（fetchBillingChargesByTenant）は最新 200 件で打ち切るため、それより古い取引の
 * 帳票 deep link を list 経由で解決すると偽の「見つかりません」になる。適格請求書は
 * 保存期間中いつでも再発行できる必要があるため、単一 doc 取得で恒久解決する
 * （useTenant / fetchTenantById と同じ O(1) 方針）。
 * tenantId が URL の値と食い違う doc は別テナントの取引のため null を返す（誤表示防止）。
 */
export async function fetchBillingChargeById(
  tenantId: string,
  chargeId: string,
  db: Firestore = defaultDb,
): Promise<BillingCharge | null> {
  const snap = await getDoc(doc(db, 'billing_charges', chargeId));
  if (!snap.exists()) {
    return null;
  }
  const data = snap.data() as Omit<BillingCharge, 'id'>;
  if (data.tenantId !== tenantId) {
    return null;
  }
  return { ...data, id: snap.id };
}
